import React, { useContext, useEffect } from "react";
import { commitMutation, fetchQuery, graphql } from "react-relay";

import {
	faCommentAltLines,
	faEllipsisV,
	faLink,
	faPen,
	faTimes,
	faTrashCan,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faArrowLeft, faCheck, faChevronDown, faChevronUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";
import { DOMParser as ProseDOMParser, Node as ProseNode } from "prosemirror-model";
import { Loader } from "semantic-ui-react";
import uuid from "uuid";

import { ViewportContext } from "~/common";
import AppContext from "~/common/AppContext";
import { transformCustomEmojiFromHTML } from "~/common/Emoji";
import useToggle from "~/lib/hooks/useToggle";
import { useSetOuterObjectContext } from "~/lib/outerObjectContext";
import { confirm } from "~/lib/prompts";
import { EditorConfig, ProseReact } from "~/lib/prose";
import { resetState, TypeAheadPluginKey } from "~/lib/prose/plugins/typeahead";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { copyToClipboard, humanizeDateAgo, nGetText } from "~/lib/utils";
import { VirtualKeyboardDetector } from "~/lib/virtualKeyboard";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import { SideBarItem } from "~/stream/SideBar/SideBarItem";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { IconButton } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import toast, { ToastActionButton } from "~/ui/toast";

import CommentsEditor from "./commentsEditor";
import { fromPathForTab } from "./threadKeys";

import type { Comments_GetShortURLMutation as GetShortURLMutation } from "~/__generated__/Comments_GetShortURLMutation.graphql";
import type {
	CommentsCreateOrUpdateCommentMutation,
	CommentThreadAccessType,
} from "~/__generated__/CommentsCreateOrUpdateCommentMutation.graphql";
import type { CommentsDeleteCommentMutation } from "~/__generated__/CommentsDeleteCommentMutation.graphql";
import type { CommentsQuery, CommentType } from "~/__generated__/CommentsQuery.graphql";
import type { CommentsReopenThreadMutation } from "~/__generated__/CommentsReopenThreadMutation.graphql";
import type { CommentsResolveThreadMutation } from "~/__generated__/CommentsResolveThreadMutation.graphql";

import styles from "./css/Comments.module.css";

const EMPTY_DOC = {
	type: "doc",
	content: [
		{
			type: "unstyled",
			attrs: {
				assignees: [],
				creator: null,
				blockId: uuid(),
				indent: 0,
				done: false,
			},
		},
	],
};

export const threadsQuery = graphql`
	query CommentsQuery($key: String!) {
		commentThreads(key: $key, resolved: false) {
			id
			key
			resolvedAt
			fullUrl

			comments {
				id
				createdAt
				editedAt
				text
				html
				type
				isDeleted

				user {
					id
					fullName
					image
					isGuest
					isExternal
				}
			}

			access {
				accessType
				addedBy {
					id
					email
					fullName
				}
				users {
					id
					fullName
					email
				}
			}
		}

		viewer {
			user {
				id
				account {
					id
				}
			}
		}
	}
`;

const createOrUpdateCommentMutation = graphql`
	mutation CommentsCreateOrUpdateCommentMutation($input: CreateOrUpdateCommentMutationInput!) {
		Comment_CreateOrUpdate(input: $input) {
			id
			thread
			text
			errors {
				field
				messages
			}
		}
	}
`;

const deleteCommentMutation = graphql`
	mutation CommentsDeleteCommentMutation($id: ID!) {
		Comment_Delete(commentId: $id) {
			comment {
				id
				isDeleted
			}
		}
	}
`;

export const resolveThreadMutation = graphql`
	mutation CommentsResolveThreadMutation($id: ID!) {
		Comment_ResolveThread(threadId: $id) {
			thread {
				id
				resolvedBy {
					id
				}
			}
		}
	}
`;

export const reopenThreadMutation = graphql`
	mutation CommentsReopenThreadMutation($id: ID!) {
		Comment_ReopenThread(threadId: $id) {
			thread {
				id
				resolvedBy {
					id
				}
			}
		}
	}
`;

export type CommentOperationWebsocketMessage = {
	type: string;
	data: {
		operation: "created" | "updated" | "deleted";
		comment?: {
			id: string;
			createdAt: string;
			editedAt: string | null;
			isDeleted: boolean;
			text: string;
			html: string;
			type: CommentType;
			user: {
				id: string;
				fullName: string;
				image: string;
				isGuest: boolean;
				isExternal: boolean;
			};
			thread: {
				id: string;
				key: string;
				resolvedAt: string | null;
				fullUrl: string | null;
				access: {
					accessType: CommentThreadAccessType;
					addedBy: {
						id: string;
						email: string;
						fullName: string;
					};
					users: Array<{
						id: string;
						email: string;
						fullName: string;
					}>;
				} | null;
			};
		};
	};
	metadata?: {
		record_id: string;
	};
};

export function createOrUpdateComment({
	commentId,
	threadKey,
	threadId,
	text,
	json,
	accessType,
	userIds,
}: {
	threadKey: string;
	commentId?: string;
	threadId?: string;
	text: string;
	json: {};
	accessType?: CommentThreadAccessType;
	userIds?: string[];
}) {
	return commitMutationPromise<CommentsCreateOrUpdateCommentMutation>(environment, {
		mutation: createOrUpdateCommentMutation,
		variables: {
			input: {
				key: threadKey,
				thread: threadId,
				text,
				json: JSON.stringify(json),
				id: commentId ? commentId : undefined,
				accessType,
				userIds,
			},
		},
	});
}

type CommentThread = NonNullable<CommentsQuery["response"]["commentThreads"]>[0];

function AddOrEditComment({
	threadKey,
	threadId,
	comment,
	afterSubmit,
	onDismiss,
	note,
}: {
	threadKey: string;
	threadId?: string;
	comment?: { id: string; text: string; html?: string };
	onDismiss?: () => void;
	afterSubmit?: () => void;
	note?: { id: string };
}) {
	const [content, setContent] = React.useState<ProseNode | { [key: string]: unknown }>(EMPTY_DOC);
	const { user } = React.useContext(AppContext);
	// Didn't have time to investigate deeper why it won't allow me type after clear
	// Use this to force a redraw
	const [clearCount, setClearCount] = React.useState(0);
	const isEmpty = !(content instanceof ProseNode) || !content.textContent;
	const editorConfigRef = React.useRef<EditorConfig>();
	const editorRef = React.useRef<ProseReact>(null);
	const [loading, setLoading] = React.useState(false);

	React.useEffect(() => {
		if (comment) {
			if (comment.html && editorConfigRef.current) {
				const container = document.createElement("div");
				container.innerHTML = comment.html;
				const node = ProseDOMParser.fromSchema(editorConfigRef.current.schema).parse(container);
				setContent(node);
			} else {
				setContent(existingContent => {
					if (existingContent.textContent) return existingContent;
					return {
						type: "doc",
						content: [
							{
								type: "unstyled",
								attrs: {
									assignees: [],
									creator: null,
									blockId: uuid(),
									indent: 0,
									done: false,
								},
								content: [{ type: "text", text: comment.text }],
							},
						],
					};
				});
			}
		}
	}, [setContent, comment]);

	const clearText = React.useCallback(() => {
		setContent({ ...EMPTY_DOC });
		setClearCount(v => v + 1);
	}, [setContent, setClearCount]);

	const askAndClear = React.useCallback(async () => {
		const needConfirmation = !comment; // Need confirmation only for new comments
		const confirmText = <p>Are you sure you want to erase your comment?</p>;

		if (needConfirmation && !(await confirm(confirmText, { title: "Confirmation required" }))) {
			return;
		}

		if (onDismiss) onDismiss();
		else if (clearText) clearText();
	}, [comment, onDismiss, clearText]);

	const onSubmit = React.useCallback(() => {
		if (!editorConfigRef.current || !editorRef || !editorRef.current || !(content instanceof ProseNode) || loading)
			return;

		let numberOfTaggedUsers = 0;
		const editorView = editorRef.current?.editorView;
		if (editorView) {
			const editorState = editorView.state;
			if (editorState) {
				const typeaheadState = TypeAheadPluginKey.getState(editorState);
				if (typeaheadState) {
					numberOfTaggedUsers = typeaheadState.results.length;
					editorView.dispatch(editorView.state.tr.setMeta(TypeAheadPluginKey, resetState(typeaheadState)));
				}
			}
		}

		setLoading(true);
		const text = content.textBetween(1, content.nodeSize - 2, "\n", "\n");
		createOrUpdateComment({
			threadKey,
			threadId,
			commentId: comment ? comment.id : undefined,
			text,
			json: content.toJSON(),
		})
			.then(() => {
				dispatchAnalyticsTrack("Comment created", {
					location: "Note",
					visibility: "everyone",
					tagsCount: numberOfTaggedUsers,
				});
				clearText();
				afterSubmit?.();
			})
			.finally(() => {
				setLoading(false);
			})
			.catch(() => {
				dispatchAnalyticsTrack("Error on comment creation", {
					location: "Note",
					visibility: "everyone",
					tagsCount: numberOfTaggedUsers,
				});
			});
	}, [content, loading, threadKey, afterSubmit, comment, clearText, threadId]);

	const virtualKeyboardDetector = React.useMemo(() => new VirtualKeyboardDetector(), []);

	React.useEffect(() => {
		window.addEventListener("keydown", virtualKeyboardDetector.onKeyDown);
		window.addEventListener("keyup", virtualKeyboardDetector.onKeyUp);
		return () => {
			window.removeEventListener("keydown", virtualKeyboardDetector.onKeyDown);
			window.removeEventListener("keyup", virtualKeyboardDetector.onKeyUp);
		};
	}, [virtualKeyboardDetector]);

	return (
		<div className={cx(styles.addReplyWrapper, { [styles.replyMode]: !comment })}>
			<div className={styles.avatar}>
				<Avatar width="24px" user={user} />
			</div>
			<div className={styles.editorWrapper}>
				<div className={cx(styles.commentActions, styles.commentButtons, { [styles.commentActionsNoText]: isEmpty })}>
					<IconButton
						size="wide"
						icon={faCheck}
						onClick={onSubmit}
						className={styles.commentActionButton}
						variant="primary"
						disabled={loading}
					/>
					<IconButton
						size="wide"
						icon={faTimes}
						onClick={onDismiss || askAndClear}
						className={styles.commentActionButton}
					/>
				</div>

				<CommentsEditor
					key={clearCount}
					onChange={setContent}
					onSubmit={onSubmit}
					doc={content}
					placeholder="Add a reply…"
					configRef={editorConfigRef}
					editorRef={editorRef}
					note={note}
					virtualKeyboardDetector={virtualKeyboardDetector}
				/>
			</div>
		</div>
	);
}

type CommentProps = {
	comment: NonNullable<CommentsQuery["response"]["commentThreads"]>[0]["comments"][0];
	currentUser?: CommentsQuery["response"]["viewer"];
	threadKey: string;
	threadId?: string;
	readOnly?: boolean;
	note?: { id: string };
	onDeletedCompleted?: () => void;
};

export function Comment({
	comment,
	currentUser,
	threadKey,
	threadId,
	readOnly,
	note,
	onDeletedCompleted,
}: CommentProps) {
	const [isEditing, toggleIsEditing] = useToggle(false);
	const [isDeleteLoading, setIsDeleteLoading] = React.useState(false);
	const [showDeleteConfirmation, toggleShowDeleteConfirmation] = useToggle(false);
	const commentAction = comment.type === "RESOLVE" ? "Resolved" : comment.type === "REOPEN" ? "Reopened" : "";
	const userLabel = comment.user.id === currentUser?.user.id ? `${comment.user.fullName} (you)` : comment.user.fullName;

	const deleteClicked = React.useCallback(() => {
		setIsDeleteLoading(true);

		commitMutation<CommentsDeleteCommentMutation>(environment, {
			mutation: deleteCommentMutation,
			variables: { id: comment.id },
			onCompleted: () => {
				setIsDeleteLoading(false);
				onDeletedCompleted?.();
			},
		});
	}, [comment, setIsDeleteLoading, onDeletedCompleted]);
	if (isEditing) {
		return (
			<AddOrEditComment
				comment={comment}
				threadKey={threadKey}
				threadId={threadId}
				afterSubmit={toggleIsEditing}
				onDismiss={toggleIsEditing}
				note={note}
			/>
		);
	}

	if (commentAction) {
		return (
			<div className={styles.comment} key={`comment-${comment.id}`}>
				<span className={styles.userTitle}>{`${commentAction} by ${userLabel}`}</span>
				<span className={styles.date}>{humanizeDateAgo(comment.createdAt || "")}</span>
			</div>
		);
	}

	if (comment.isDeleted) {
		return (
			<div className={cx(styles.comment, styles.deleted)} key={`comment-${comment.id}`}>
				<div className={styles.deletedIcon}>
					<FontAwesomeIcon icon={faTrashCan} />
				</div>
				<div>This message was deleted.</div>
			</div>
		);
	}

	if (showDeleteConfirmation) {
		return (
			<div className={styles.comment} key={`comment-${comment.id}`}>
				<div className={styles.confirmation}>Are you sure you want to delete this comment? This can’t be undone.</div>
				<div className={styles.deleteButtons}>
					<Button size="normal" variant="destructive" onClick={deleteClicked}>
						Delete
					</Button>

					<Button size="normal" onClick={toggleShowDeleteConfirmation}>
						Cancel
					</Button>
				</div>
			</div>
		);
	}

	const canEdit = !readOnly && comment.user.id === currentUser?.user.id;

	return (
		<div className={styles.comment} key={`comment-${comment.id}`}>
			<div className={styles.commentHeader}>
				<Avatar className={styles.avatar} user={comment.user} width="24px" />

				<div>
					<span className={styles.userTitle}>{userLabel}</span>
					<span className={styles.date}>{humanizeDateAgo(comment.createdAt || "")}</span>
				</div>
				{canEdit && (
					<div className={styles.commentActions}>
						{isDeleteLoading ? (
							<Loader inline active size="small" />
						) : (
							<Menu mobileHeaderTitle="Comment actions">
								<Menu.Trigger>
									<IconButton title="Actions" icon={faEllipsisV} />
								</Menu.Trigger>
								<Menu.Content align="end" side="bottom">
									<Menu.Item icon={faPen} onSelect={toggleIsEditing}>
										Edit
									</Menu.Item>
									<Menu.Item icon={faTrashCan} onSelect={toggleShowDeleteConfirmation} destructive>
										Delete
									</Menu.Item>
								</Menu.Content>
							</Menu>
						)}
					</div>
				)}
			</div>

			{comment.html ? <CommentHTML html={comment.html} /> : <div className={styles.commentText}>{comment.text}</div>}
		</div>
	);
}

export function CommentHTML({ html }: { html: string }) {
	const transformedHTML = React.useMemo(() => {
		return transformCustomEmojiFromHTML(html);
	}, [html]);
	return (
		<div className={cx(styles.commentText, "ProseMirror")} dangerouslySetInnerHTML={{ __html: transformedHTML }} />
	);
}

function SetCommentThreadOuterObjectId({ commentThreadId }: { commentThreadId: string }) {
	useSetOuterObjectContext(commentThreadId);
	return <></>;
}

function CommentsEmptyState() {
	return (
		<div className={styles.commentsEmptyState}>
			<FontAwesomeIcon icon={faCommentAltLines} />
			Add a comment
		</div>
	);
}

type CommentsPanelProps = {
	threadKey: string;
	closePanel(): void;
	intendToAdd?: boolean;
	noteId: string;
};

export function CommentsPanel({ threadKey, closePanel, noteId }: CommentsPanelProps) {
	const [result, setResult] = React.useState<CommentsQuery["response"] | undefined>();
	const [currentThreadIndex, setCurrentThreadIndex] = React.useState(0);
	const [resolveLoading, setResolveLoading] = React.useState(false);
	const groupKey = `comments.${threadKey.split(":").join("-")}`;

	const updateGroupMetadata = React.useCallback(
		(override: boolean) => {
			WebsocketClient.getInstance().updateGroupMetadata(groupKey, override, {
				lastEntityId: Date.now().toString(),
			});
		},
		[groupKey],
	);

	const commentsContainerRef = React.useRef<null | HTMLDivElement>(null);
	const scrollToBottom = React.useCallback(() => {
		commentsContainerRef.current?.scrollTo({ top: 9999 }); // If set to a value greater than max, this goes to max
	}, []);

	const fetchData = React.useCallback(() => {
		setResult(undefined);
		setCurrentThreadIndex(0);
		fetchQuery<CommentsQuery>(environment, threadsQuery, { key: threadKey }, { fetchPolicy: "network-only" })
			.toPromise()
			.then(data => {
				setResult(data);
				scrollToBottom();
			});
	}, [threadKey, scrollToBottom]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	let threadsCount = 0;
	let currentThread: CommentThread | null = null;
	if (result && result.commentThreads && currentThreadIndex < result.commentThreads.length) {
		threadsCount = result.commentThreads.length;
		currentThread = result.commentThreads[currentThreadIndex];
	}

	const threadUp = React.useCallback(() => {
		setCurrentThreadIndex(idx => (idx > 0 ? idx - 1 : idx));
	}, []);

	const threadDown = React.useCallback(() => {
		setCurrentThreadIndex(idx => (idx < threadsCount - 1 ? idx + 1 : idx));
	}, [threadsCount]);

	const reopenPressed = React.useCallback(() => {
		let currentThread: CommentThread | null = null;
		if (result && result.commentThreads && currentThreadIndex < result.commentThreads.length) {
			currentThread = result.commentThreads[currentThreadIndex];
		}

		if (resolveLoading || !currentThread) {
			return;
		}

		setResolveLoading(true);

		commitMutation<CommentsReopenThreadMutation>(environment, {
			mutation: reopenThreadMutation,
			variables: { id: currentThread.id },
			onCompleted: () => {
				setResolveLoading(false);

				toast.success(<p>You can view the comments in the note.</p>, {
					title: "Thread reopened",
					closeButton: true,
				});
			},
		});
	}, [resolveLoading, result, currentThreadIndex]);

	const resolvePressed = React.useCallback(() => {
		let currentThread: CommentThread | null = null;
		if (result && result.commentThreads && currentThreadIndex < result.commentThreads.length) {
			currentThread = result.commentThreads[currentThreadIndex];
		}

		if (resolveLoading || !currentThread) {
			return;
		}

		setResolveLoading(true);

		commitMutation<CommentsResolveThreadMutation>(environment, {
			mutation: resolveThreadMutation,
			variables: { id: currentThread.id },
			onCompleted: () => {
				setResolveLoading(false);
				closePanel();

				const toastId = toast.success(<p>You can view resolved comments from the note menu.</p>, {
					title: "Thread resolved",
					closeButton: true,
					actions: (
						<>
							<ToastActionButton
								// eslint-disable-next-line react/jsx-no-bind
								onClick={() => {
									toast.dismiss(toastId);
									reopenPressed();
								}}
							>
								Reopen
							</ToastActionButton>
						</>
					),
				});
				const unlisten = window.HISTORY.listen(() => {
					unlisten();
					toast.dismiss(toastId);
				});
			},
		});
	}, [closePanel, resolveLoading, result, currentThreadIndex, reopenPressed]);

	const onWebsocketMessage = React.useCallback(
		(data: CommentOperationWebsocketMessage) => {
			if (data.type === "COMMENTS_UPDATED") {
				updateGroupMetadata(true);
				const commentData = data.data.comment;
				if (commentData) {
					if (commentData.type === "REOPEN") {
						fetchData();
					} else {
						setResult(currentResult => {
							if (!currentResult) return;
							const newComment = commentData;
							const threads = currentResult.commentThreads;
							if (!threads || !newComment) return currentResult;

							let threadToChange;
							const threadToChangeIndex = threads.findIndex(t => t.id === newComment.thread?.id);
							if (threadToChangeIndex === -1) {
								threadToChange = {
									...commentData.thread,
									comments: [],
								};
							} else {
								threadToChange = threads[threadToChangeIndex];
							}

							const firstThreads = threads.slice(0, threadToChangeIndex);
							const lastThreads = threads.slice(threadToChangeIndex + 1);

							const existingThreadComments = threadToChange.comments;
							const existingCommentIndex = existingThreadComments.findIndex(c => c.id === newComment.id);
							let newComments = [...existingThreadComments];
							if (existingCommentIndex > -1) {
								newComments.splice(existingCommentIndex, 1, newComment);
							} else {
								newComments.push(newComment);
							}

							return {
								commentThreads: [
									...firstThreads,
									{
										...threadToChange,
										...newComment.thread,
										comments: newComments,
									},
									...lastThreads,
								],
								viewer: currentResult.viewer,
							};
						});
					}
				}
			}
		},
		[setResult, fetchData, updateGroupMetadata],
	);

	React.useEffect(() => {
		const socket = WebsocketClient.getInstance().getOrJoinGroup(groupKey, {});
		socket.on("message", onWebsocketMessage);
		const onOpen = () => {
			updateGroupMetadata(false);
		};
		socket.on("open", onOpen);

		return () => {
			socket.off("message", onWebsocketMessage);
			socket.off("open", onOpen);
			socket.close();
		};
	}, [threadKey, onWebsocketMessage, fetchData, groupKey, updateGroupMetadata]);

	const copyLink = React.useCallback(async () => {
		if (!currentThread?.id) return;
		const nonnullThreadId = currentThread.id;

		async function getShortUrl() {
			const result = await commitMutationPromise<GetShortURLMutation>(environment, {
				mutation: graphql`
					mutation Comments_GetShortURLMutation($threadId: ID!) {
						Comment_GetShortURL(threadId: $threadId) {
							url
						}
					}
				`,
				variables: { threadId: nonnullThreadId },
			});
			if (!result.Comment_GetShortURL) throw new Error();
			return result.Comment_GetShortURL.url;
		}

		try {
			await copyToClipboard(getShortUrl());
			toast.success("Copied link to clipboard");
		} catch (e) {
			Sentry.captureException(e);
			console.error(e);
			toast.error("Failed to copy link, please try again later");
		}
	}, [currentThread?.id]);

	const commentsCount = currentThread?.comments.filter(c => c.type === "TEXT" && !c.isDeleted).length;

	const viewportContext = useContext(ViewportContext);

	return (
		<SideBarItem className={styles.panel}>
			{/* useSetOuterObjectContext is wrapped in a component since a hook can't
			    be called conditionally depending on if currentThread is null or not. */}
			{currentThread && <SetCommentThreadOuterObjectId commentThreadId={currentThread.id} />}
			<SideBarItem.Header className={styles.panelHeaderWrapper}>
				<SideBarItem.Title>
					{threadsCount > 1 ? (
						<div className={styles.multithreads}>
							{currentThreadIndex + 1} of {threadsCount}
							<div className={styles.arrows}>
								<FontAwesomeIcon className={styles.arrow} icon={faChevronUp} onClick={threadUp} />
								<FontAwesomeIcon className={styles.arrow} icon={faChevronDown} onClick={threadDown} />
							</div>
						</div>
					) : currentThread ? (
						`${commentsCount} ${nGetText(commentsCount || 0, "comment", "comments")}`
					) : result ? (
						"0 comments"
					) : (
						"Loading..."
					)}
				</SideBarItem.Title>

				{viewportContext.isMobile && (
					<Button size="slim" variant="secondary" onClick={closePanel}>
						<FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: "0.857em" }} />
						&ensp;Back to notes
					</Button>
				)}
				{currentThread && (
					<Menu>
						<Menu.Trigger>
							<IconButton style={{ fontSize: "1rem" }} icon={faEllipsisV} />
						</Menu.Trigger>
						<Menu.Content align="end" side="bottom">
							<Menu.Item onSelect={copyLink} icon={faLink}>
								Copy link
							</Menu.Item>
							{currentThread.resolvedAt ? (
								<Menu.Item onSelect={reopenPressed} icon={faCheck}>
									Reopen
								</Menu.Item>
							) : (
								<Menu.Item onSelect={resolvePressed} icon={faCheck}>
									Resolve
								</Menu.Item>
							)}
						</Menu.Content>
					</Menu>
				)}
			</SideBarItem.Header>

			<SideBarItem.Body ref={commentsContainerRef} className={cx(styles.commentsContainer)}>
				{currentThread ? (
					currentThread.comments.map(
						comment =>
							!!currentThread && (
								<Comment
									key={`comment-${comment.id}`}
									comment={comment}
									currentUser={result?.viewer}
									threadKey={currentThread.key}
									threadId={currentThread.id}
									note={{ id: noteId }}
								/>
							),
					)
				) : (
					<CommentsEmptyState />
				)}
				<div className={styles.commentsAnchor} />
			</SideBarItem.Body>
			{!currentThread?.resolvedAt && (
				<AddOrEditComment
					threadKey={threadKey}
					onDismiss={!currentThread ? closePanel : undefined}
					threadId={currentThread?.id}
					note={{ id: noteId }}
					afterSubmit={scrollToBottom}
				/>
			)}
		</SideBarItem>
	);
}

export function CommentsTab({ tabIdPath, closePanel }: { tabIdPath: string; closePanel(): void }) {
	const { noteId, threadKey } = React.useMemo(() => fromPathForTab(tabIdPath), [tabIdPath]);

	return <CommentsPanel threadKey={threadKey} closePanel={closePanel} noteId={noteId} />;
}
